import { useEffect, useRef, useState } from 'react';
import { ALL_FORMATS, BlobSource, CanvasSink, Input } from 'mediabunny';

const cache = new Map<string, string>();
const keyOf = (file: File, t: number) => `${file.name}:${file.size}:${Math.round(t)}`;

async function toUrl(canvas: HTMLCanvasElement | OffscreenCanvas): Promise<string> {
  const blob = 'convertToBlob' in canvas ? await canvas.convertToBlob({ type: 'image/jpeg', quality: 0.7 }) : await new Promise<Blob | null>((r) => canvas.toBlob(r, 'image/jpeg', 0.7));
  return blob ? URL.createObjectURL(blob) : '';
}

/**
 * Small thumbnails for a list of video times, decoded in the background one after another.
 * Returns a map from rounded second to an image URL.
 */
export function useThumbnails(file: File | null, times: number[]): Map<number, string> {
  const [, setTick] = useState(0);
  const wanted = useRef<number[]>([]);
  const signature = times.map((t) => Math.round(t)).join(',');

  useEffect(() => {
    if (!file) return;
    const todo = [...new Set(times.map((t) => Math.round(t)))].filter((t) => !cache.has(keyOf(file, t))).sort((a, b) => a - b);
    wanted.current = todo;
    if (!todo.length) return;
    let stop = false;
    const input = new Input({ source: new BlobSource(file), formats: ALL_FORMATS });
    (async () => {
      const track = await input.getPrimaryVideoTrack();
      if (!track || !(await track.canDecode())) return;
      const sink = new CanvasSink(track, { width: 192, height: 108, fit: 'cover' });
      let n = 0;
      // A frame a second into the clip is more telling than the first one.
      for await (const wrapped of sink.canvasesAtTimestamps(todo.map((t) => t + 1))) {
        if (stop) break;
        const t = todo[n++];
        if (wrapped) {
          cache.set(keyOf(file, t), await toUrl(wrapped.canvas));
          setTick((x) => x + 1);
        }
      }
    })()
      .catch(() => {
        /* thumbnails are a nicety; the pitch placeholder stays */
      })
      .finally(() => input.dispose());
    return () => {
      stop = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file, signature]);

  const out = new Map<number, string>();
  if (file) for (const t of times) {
    const url = cache.get(keyOf(file, Math.round(t)));
    if (url) out.set(Math.round(t), url);
  }
  return out;
}
